import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type Plugin,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { WebsiteReportDay } from '../../lib/websiteVisitsReport';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const hoverGuide: Plugin = {
  id: 'websiteVisitsHoverGuide',
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements() ?? [];
    if (!active.length) return;
    const x = active[0].element.x;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(107, 114, 128, 0.35)';
    ctx.setLineDash([2, 3]);
    ctx.stroke();
    ctx.restore();
  },
};

function formatChartDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Shopify-style website visits line. The last segment is dotted when the day is still open. */
export default function WebsiteVisitsLineChart({
  days,
  mode,
  showConversions,
  openDay,
  maxTicks = 8,
}: {
  days: WebsiteReportDay[];
  mode: 'visits' | 'unique';
  showConversions: boolean;
  openDay: boolean;
  maxTicks?: number;
}) {
  const labels = days.map((day) => day.label);
  const current = days.map((day) => (mode === 'visits' ? day.visits : day.users));
  const previous = days.map((day) => (mode === 'visits' ? day.previousVisits : day.previousUsers));
  const lastIndex = Math.max(current.length - 1, 0);
  const currentColor = mode === 'visits' ? 'rgb(37, 99, 235)' : 'rgb(126, 34, 206)';
  const currentLabel = mode === 'visits'
    ? 'Current Period (Daily Visits)'
    : 'Current Period (Unique Daily Visitors)';
  const previousLabel = mode === 'visits'
    ? 'Previous Period (Daily Visits)'
    : 'Previous Period (Unique Daily Visitors)';
  const openDayDash = (ctx: { p1DataIndex: number }) =>
    openDay && lastIndex > 0 && ctx.p1DataIndex === lastIndex ? [1.5, 5] : undefined;

  const datasets = [
    {
      label: currentLabel,
      data: current,
      borderColor: currentColor,
      borderWidth: 2,
      borderCapStyle: 'round' as const,
      tension: 0.5,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBackgroundColor: currentColor,
      pointBorderColor: 'white',
      pointBorderWidth: 2,
      pointHoverBorderWidth: 2,
      segment: { borderDash: openDayDash },
      actualDates: days.map((day) => day.date),
    },
    {
      label: previousLabel,
      data: previous,
      borderColor: 'rgba(147, 197, 253, 0.95)',
      borderWidth: 1.5,
      borderCapStyle: 'round' as const,
      borderDash: [1.5, 5],
      tension: 0.5,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointBackgroundColor: 'rgb(147, 197, 253)',
      pointBorderColor: 'white',
      pointBorderWidth: 2,
      pointHoverBorderWidth: 2,
      actualDates: days.map((day) => day.previousDate),
    },
    ...(showConversions
      ? [{
          label: 'Conversions',
          data: days.map((day) => day.conversions),
          borderColor: 'rgb(202, 138, 4)',
          borderWidth: 2,
          borderCapStyle: 'round' as const,
          tension: 0.5,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointBackgroundColor: 'rgb(202, 138, 4)',
          pointBorderColor: 'white',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 2,
          yAxisID: 'y1',
          segment: { borderDash: openDayDash },
          actualDates: days.map((day) => day.date),
        }]
      : []),
  ];

  return (
    <Line
      plugins={[hoverGuide]}
      data={{ labels, datasets }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 16,
              boxHeight: 2,
              font: { size: 10 },
              padding: 8,
              color: '#6b7280',
            },
          },
          tooltip: {
            backgroundColor: 'rgb(23, 23, 23)',
            titleColor: '#ffffff',
            bodyColor: '#e5e7eb',
            padding: 12,
            cornerRadius: 10,
            displayColors: true,
            usePointStyle: true,
            callbacks: {
              title: (context) => {
                const dataset = context[0]?.dataset as { actualDates?: string[] };
                const pointIndex = context[0]?.dataIndex;
                const iso = dataset?.actualDates?.[pointIndex ?? -1];
                return iso ? formatChartDate(iso) : context[0]?.label ?? '';
              },
              label: (context) => {
                const name = context.dataset.label ?? '';
                const unit = name === 'Conversions'
                  ? 'conversions'
                  : name.includes('Unique')
                    ? 'visitors'
                    : 'visits';
                const value = Number(context.parsed.y ?? 0).toLocaleString();
                return `${name}: ${value} ${unit}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: 'rgba(0, 0, 0, 0.06)', drawTicks: false },
            ticks: {
              callback: (value) => `${value}`,
              font: { size: 10 },
              color: '#9ca3af',
              padding: 8,
            },
          },
          ...(showConversions
            ? {
                y1: {
                  position: 'right' as const,
                  beginAtZero: true,
                  border: { display: false },
                  grid: { drawOnChartArea: false },
                  ticks: {
                    color: 'rgb(161, 98, 7)',
                    font: { size: 10 },
                    precision: 0,
                    padding: 8,
                  },
                },
              }
            : {}),
          x: {
            border: { display: false },
            grid: { display: false },
            ticks: {
              font: { size: 10 },
              color: '#9ca3af',
              maxTicksLimit: maxTicks,
              maxRotation: 0,
            },
          },
        },
      }}
    />
  );
}
